/**
 * Générateur — Automatisme : reconnaître et construire le symétrique d'une
 * figure par symétrie axiale, dont l'axe est vertical, horizontal, ou en
 * diagonale sur quadrillage.
 * Attendu ID : 1070 | Niveau : 5ème | Domaine : Transformations
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Combien de carreaux ?           (saisie : distance à l'axe, du symétrique, entre les deux)
 *   - Le bon symétrique               (QCM planche ①-④ ; V/F sur la figure)
 *   - Symétrie axiale : vrai ou faux ? (V/F pool + deux lignes numériques)
 *
 * ── Plan de cartes validé (2026-09-04) ─────────────────────────────────────
 * Source : Automatisme n°32 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : INTRODUITE au CM1 (compléter, axe vertical / horizontal), CONSOLIDÉE au
 * CM2 (construire, axe vertical, horizontal ou diagonale du quadrillage — texte
 * du BO), FORMALISÉE en 6e (définition, propriétés), automatisme en 5e.
 * P2 : sens installés — le pliage, le report de carreaux PERPENDICULAIREMENT à
 * l'axe (le long de l'autre diagonale quand l'axe est en diagonale), les
 * conservations (longueurs, angles, aires), le point sur l'axe est lui-même.
 * Distracteurs : copie translatée du même côté, report du mauvais nombre de
 * carreaux, report horizontal quand l'axe est en diagonale.
 * P3 : nombres de carreaux entiers, doubles. P5 : quadrillage SANS repère
 * (le repère du plan n'existe pas avant la 5e), axe tracé, sommets sur les nœuds.
 * P6 : aucun instrument, on compte. P7 : pas de symétrie centrale (limite de la
 * fiche), pas d'axe quelconque. P8 : symétrique, axe de symétrie, A′.
 * P10 : constat sur quadrillage ; les propriétés sont admises et se vérifient.
 * P11 : en diagonale on ne compte plus horizontalement ; le point sur l'axe est
 * son propre symétrique. P12 : rémanence forte, pas d'item de réactivation.
 * INVARIANTS :
 *   - tout est CALCULÉ sur nœuds entiers : symétrique exact (formules par type
 *     d'axe), distances en carreaux entières (parité contrôlée en diagonale),
 *     figures et symétriques dans la grille, jamais à cheval sur l'axe ;
 *   - les constructions fausses de la planche sont DISTINCTES de la bonne et
 *     entre elles, toutes dans la grille ;
 *   - data-axe, data-points, data-figure, data-candidats dans le SVG pour les
 *     solveurs ; réponses ENTIÈRES ; pas de calculatrice ;
 *   - V/F : fenêtre stricte des 4 dernières lignes fixes servies.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1070;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CARREAUX = 'Combien de carreaux ?';
const L_BON = 'Le bon symétrique';
const L_VF = 'Symétrie axiale : vrai ou faux ?';

// ─── Axes et symétries sur nœuds entiers ───────────────────────────────────
// Grille N × N (nœuds 0..N). Axe : { type: 'v' | 'h' | 'd1' | 'd2', c }
//   v  : x = c            h  : y = c
//   d1 : y = x + c        d2 : x + y = c
const N = 8;

const symetrique = (P, axe) => {
    const [x, y] = P; const { type, c } = axe;
    if (type === 'v') return [2 * c - x, y];
    if (type === 'h') return [x, 2 * c - y];
    if (type === 'd1') return [y - c, x + c];
    return [c - y, c - x];
};

/** Distance à l'axe en carreaux (en diagonale : le long de l'autre diagonale). Entière si la parité convient. */
const distanceAxe = (P, axe) => {
    const [x, y] = P; const { type, c } = axe;
    if (type === 'v') return Math.abs(x - c);
    if (type === 'h') return Math.abs(y - c);
    if (type === 'd1') return Math.abs(y - x - c) / 2;
    return Math.abs(x + y - c) / 2;
};

const surAxe = (P, axe) => distanceAxe(P, axe) === 0;
const dansGrille = (P) => P[0] >= 0 && P[0] <= N && P[1] >= 0 && P[1] <= N;
const cle = (P) => `${P[0]},${P[1]}`;

const tirerAxe = (type = null) => {
    const t = type || randomChoice(['v', 'h', 'd1', 'd2']);
    if (t === 'v' || t === 'h') return { type: t, c: randInt(3, N - 3) };
    if (t === 'd1') return { type: t, c: randInt(-2, 2) };        // y = x + c
    return { type: t, c: randInt(N - 2, N + 2) };                  // x + y = c
};

/** Point à distance ENTIÈRE k (≥ 1) de l'axe, avec son symétrique dans la grille. */
const tirerPoint = (axe, kMin = 1, kMax = 4) => {
    for (let essai = 0; essai < 500; essai++) {
        const P = [randInt(0, N), randInt(0, N)];
        const k = distanceAxe(P, axe);
        if (!Number.isInteger(k) || k < kMin || k > kMax) continue;
        const S = symetrique(P, axe);
        if (!dansGrille(S)) continue;
        return P;
    }
    throw new Error('gen_1070 : point introuvable');
};

// ─── Rendu SVG ─────────────────────────────────────────────────────────────

const CELL = 24; const PAD = 22;
const f1 = (v) => v.toFixed(1);

/** Segment de l'axe qui traverse toute la grille (coordonnées grille). */
const segmentAxe = (axe) => {
    const { type, c } = axe;
    if (type === 'v') return [[c, 0], [c, N]];
    if (type === 'h') return [[0, c], [N, c]];
    if (type === 'd1') { const pts = []; for (let x = -N; x <= 2 * N; x++) { const y = x + c; if (x >= 0 && x <= N && y >= 0 && y <= N) pts.push([x, y]); } return [pts[0], pts[pts.length - 1]]; }
    const pts = []; for (let x = -N; x <= 2 * N; x++) { const y = c - x; if (x >= 0 && x <= N && y >= 0 && y <= N) pts.push([x, y]); } return [pts[0], pts[pts.length - 1]];
};

/**
 * Dessine une grille avec axe, points nommés et polygones, à l'offset (ox, oy).
 * cfg = { axe, points: [{p, nom, couleur}], polys: [{pts, couleur, dash}], echelle }
 */
const dessinerGrille = (cfg, ox = 0, oy = 0, cell = CELL) => {
    const X = (x) => ox + PAD + x * cell; const Y = (y) => oy + PAD + (N - y) * cell;
    let s = '';
    for (let i = 0; i <= N; i++) {
        s += `<line x1="${f1(X(i))}" y1="${f1(Y(0))}" x2="${f1(X(i))}" y2="${f1(Y(N))}" stroke="#cbd5e1" stroke-width="1"/>`;
        s += `<line x1="${f1(X(0))}" y1="${f1(Y(i))}" x2="${f1(X(N))}" y2="${f1(Y(i))}" stroke="#cbd5e1" stroke-width="1"/>`;
    }
    const [a1, a2] = segmentAxe(cfg.axe);
    s += `<line x1="${f1(X(a1[0]))}" y1="${f1(Y(a1[1]))}" x2="${f1(X(a2[0]))}" y2="${f1(Y(a2[1]))}" stroke="#dc2626" stroke-width="2.4" data-axe="${cfg.axe.type}:${cfg.axe.c}"/>`;
    if (cfg.nomAxe !== false) {
        const L = cfg.axe.type === 'v' ? [X(a2[0]) + 10, Y(a2[1]) - 6] : cfg.axe.type === 'h' ? [X(a2[0]) + 10, Y(a2[1]) + 4] : [X(a2[0]) + (cfg.axe.type === 'd1' ? 8 : 10), Y(a2[1]) + (cfg.axe.type === 'd1' ? -4 : 12)];
        s += `<text x="${f1(L[0])}" y="${f1(L[1])}" font-size="${cell >= 20 ? 13 : 11}" font-weight="700" fill="#dc2626">(d)</text>`;
    }
    (cfg.polys || []).forEach((pl) => {
        const pts = pl.pts.map((p) => `${f1(X(p[0]))},${f1(Y(p[1]))}`).join(' ');
        s += `<polygon points="${pts}" fill="${pl.couleur}" fill-opacity="0.18" stroke="${pl.couleur}" stroke-width="2"${pl.dash ? ' stroke-dasharray="5 3"' : ''} data-poly="${pl.pts.map(cle).join('|')}" data-role="${pl.role || ''}"/>`;
    });
    (cfg.points || []).forEach((pt) => {
        const cx = X(pt.p[0]); const cy = Y(pt.p[1]); const col = pt.couleur || '#1E3A8A';
        s += `<circle cx="${f1(cx)}" cy="${f1(cy)}" r="${cell >= 20 ? 4 : 3}" fill="${col}" data-point="${pt.nom}" data-xy="${cle(pt.p)}"/>`;
        // étiquette : en haut à droite, sauf si ça sort de la grille à droite
        const dx = pt.p[0] >= N - 0.5 ? -10 : 7; const dy = pt.p[1] >= N - 0.5 ? 14 : -6;
        s += `<text x="${f1(cx + dx)}" y="${f1(cy + dy)}" font-size="${cell >= 20 ? 13 : 11}" font-weight="700" fill="${col}" text-anchor="${dx < 0 ? 'end' : 'start'}">${pt.nom}</text>`;
    });
    return s;
};

const svgUneGrille = (cfg) => {
    const W = 2 * PAD + N * CELL + 26; const H = 2 * PAD + N * CELL;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-axe="${cfg.axe.type}:${cfg.axe.c}" data-points="${(cfg.points || []).map((pt) => `${pt.nom}:${cle(pt.p)}`).join('|')}">${dessinerGrille(cfg)}</svg>`;
};

const NOM_AXE = { v: 'vertical', h: 'horizontal', d1: 'en diagonale', d2: 'en diagonale' };
const PRIME = '′';

// ─── Carte 1 : combien de carreaux ? (saisie) ──────────────────────────────

let derniereSignatureCarreaux1070 = null;

const genCarreauxSaisie = () => {
    let payload = construireCarreauxSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureCarreaux1070; t++) payload = construireCarreauxSaisie();
    derniereSignatureCarreaux1070 = payload._meta.params.signature;
    return payload;
};

const construireCarreauxSaisie = () => {
    const sousType = randomChoice(['distance', 'distance_symetrique', 'entre']);
    // La distance k est tirée D'ABORD (1 à 4 à parts égales, revue 2026-09-04, A2), puis un point à cette distance.
    let axe; let A = null; let kVoulu;
    for (let essai = 0; essai < 50 && !A; essai++) {
        axe = tirerAxe(); kVoulu = randInt(1, 4);
        try { A = tirerPoint(axe, kVoulu, kVoulu); } catch { A = null; }
    }
    if (!A) throw new Error('gen_1070 : point à distance voulue introuvable');
    const Ap = symetrique(A, axe); const k = distanceAxe(A, axe);
    if (!Number.isInteger(k) || distanceAxe(Ap, axe) !== k) throw new Error('gen_1070 : distance non entière');
    const diag = axe.type === 'd1' || axe.type === 'd2';
    const nomA = `A${PRIME}`;
    const precision = diag ? ' L\'axe est en diagonale : on compte les carreaux le long de l\'autre diagonale, perpendiculairement à l\'axe.' : '';
    let texte; let reponse;
    const intro = `<p>Sur le quadrillage, la droite (d) est un axe ${NOM_AXE[axe.type]}. Le point ${nomA} est le symétrique de A par rapport à (d).${precision}</p>`;
    if (sousType === 'distance') { texte = `${intro}<p>À combien de carreaux de l'axe (d) se trouve le point A ?</p>`; reponse = k; }
    else if (sousType === 'distance_symetrique') { texte = `${intro}<p>À combien de carreaux de l'axe (d) se trouve le point ${nomA} ?</p>`; reponse = k; }
    else { texte = `${intro}<p>Combien de carreaux séparent A de ${nomA}, en allant tout droit de l'un à l'autre ?</p>`; reponse = 2 * k; }
    const svg = svgUneGrille({ axe, points: [{ p: A, nom: 'A' }, { p: Ap, nom: nomA, couleur: '#B45309' }] });
    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_CARREAUX), suffixe: 'carreaux', elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, axe, A, k, reponse, signature: `${sousType}-${axe.type}${axe.c}-${cle(A)}` };
    return payload;
};

// ─── Carte 2a : le bon symétrique (QCM planche ①-④) ────────────────────────

/** Petit triangle à sommets entiers d'un même côté de l'axe, à au moins 1 carreau, symétrique dans la grille. */
const tirerFigure = (axe) => {
    for (let essai = 0; essai < 500; essai++) {
        const O = tirerPoint(axe, 1, 3);
        const cand = [[O[0] + randomChoice([1, 2]), O[1]], [O[0], O[1] + randomChoice([1, 2])], [O[0] + 1, O[1] + 2], [O[0] + 2, O[1] + 1], [O[0] - 1, O[1] + 2]];
        const B = randomChoice(cand); const C = randomChoice(cand.filter((p) => cle(p) !== cle(B)));
        const pts = [O, B, C];
        if (!pts.every(dansGrille)) continue;
        // même côté de l'axe (signe), aucun sommet sur l'axe, symétrique dans la grille
        const signe = (P) => { const [x, y] = P; const { type, c } = axe; return Math.sign(type === 'v' ? x - c : type === 'h' ? y - c : type === 'd1' ? y - x - c : x + y - c); };
        if (pts.some((p) => signe(p) === 0) || new Set(pts.map(signe)).size !== 1) continue;
        if (!pts.map((p) => symetrique(p, axe)).every(dansGrille)) continue;
        // non aplati
        const aire2 = Math.abs((B[0] - O[0]) * (C[1] - O[1]) - (C[0] - O[0]) * (B[1] - O[1]));
        if (aire2 === 0) continue;
        return pts;
    }
    throw new Error('gen_1070 : figure introuvable');
};

const normale = (axe) => (axe.type === 'v' ? [1, 0] : axe.type === 'h' ? [0, 1] : axe.type === 'd1' ? [1, -1] : [1, 1]);
const translater = (pts, v) => pts.map((p) => [p[0] + v[0], p[1] + v[1]]);
const memeFigure = (F, G) => F.map(cle).sort().join('|') === G.map(cle).sort().join('|');

/** Les quatre constructions : la bonne et trois fausses distinctes, toutes dans la grille. */
const construireCandidats = (F, axe) => {
    const S = F.map((p) => symetrique(p, axe));
    const n = normale(axe);
    const signe = (P) => { const [x, y] = P; const { type, c } = axe; return Math.sign(type === 'v' ? x - c : type === 'h' ? y - c : type === 'd1' ? y - x - c : x + y - c); };
    const versLoin = signe(S[0]) > 0 ? n : [-n[0], -n[1]];   // s'éloigner de l'axe côté S
    const candidats = [];
    // 1. copie translatée du même côté que S (pas retournée)
    const dMin = Math.min(...F.map((p) => distanceAxe(p, axe)));
    const T = translater(F, [versLoin[0] * 2 * dMin, versLoin[1] * 2 * dMin]);
    candidats.push({ pts: T, role: 'translation' });
    // 2. un carreau de trop
    candidats.push({ pts: translater(S, versLoin), role: 'distance' });
    // 3. report horizontal (axe en diagonale) ou un sommet mal reporté (axe droit)
    if (axe.type === 'd1' || axe.type === 'd2') {
        const R = F.map(([x, y]) => { const xAxe = axe.type === 'd1' ? y - axe.c : axe.c - y; return [2 * xAxe - x, y]; });
        candidats.push({ pts: R, role: 'horizontal' });
    } else {
        const R = S.map((p, i) => (i === 0 ? [p[0] + versLoin[0], p[1] + versLoin[1]] : p));
        candidats.push({ pts: R, role: 'sommet' });
    }
    // Gardes : dans la grille, distinctes de S et entre elles ; sinon on renvoie null (retirage)
    const tous = [S, ...candidats.map((c) => c.pts)];
    if (!tous.every((pts) => pts.every(dansGrille))) return null;
    for (let i = 0; i < tous.length; i++) for (let j = i + 1; j < tous.length; j++) if (memeFigure(tous[i], tous[j])) return null;
    return { S, faux: candidats };
};

const svgPlanche = (axe, F, panneaux) => {
    const cell = 16; const w = 2 * PAD + N * cell + 10; const h = 2 * PAD + N * cell;
    const numeros = ['①', '②', '③', '④'];
    let s = '';
    panneaux.forEach((pan, i) => {
        const ox = (i % 2) * w; const oy = Math.floor(i / 2) * (h + 4);
        s += `<g data-index="${i + 1}" data-candidat="${pan.pts.map(cle).join('|')}" data-role="${pan.role}">`
            + `<text x="${f1(ox + 12)}" y="${f1(oy + 16)}" font-size="15" font-weight="800" fill="#1E3A8A">${numeros[i]}</text>`
            + dessinerGrille({ axe, nomAxe: false, polys: [{ pts: F, couleur: '#1E3A8A' }, { pts: pan.pts, couleur: '#B45309', dash: true, role: pan.role }] }, ox, oy, cell)
            + '</g>';
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${2 * w} ${2 * h + 4}" width="${2 * w}" height="${2 * h + 4}" font-family="Arial" data-axe="${axe.type}:${axe.c}" data-figure="${F.map(cle).join('|')}">${s}</svg>`;
};

let derniereSignatureBon1070 = null;

const genBonQcm = () => {
    let payload = construireBonQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureBon1070; t++) payload = construireBonQcm();
    derniereSignatureBon1070 = payload._meta.params.signature;
    return payload;
};

const construireBonQcm = () => {
    // Le TYPE d'axe est tiré d'abord (quatre types à parts égales, revue 2026-09-04, A8) ;
    // seuls la position de l'axe et la figure sont retirés tant que les quatre constructions ne tiennent pas.
    const type = randomChoice(['v', 'h', 'd1', 'd2']);
    let axe; let F; let cands = null;
    for (let essai = 0; essai < 400 && !cands; essai++) { axe = tirerAxe(type); F = tirerFigure(axe); cands = construireCandidats(F, axe); }
    if (!cands) throw new Error('gen_1070 : candidats introuvables');
    const panneaux = shuffle([{ pts: cands.S, role: 'symetrique' }, ...cands.faux]);
    const bonneIndex = panneaux.findIndex((p) => p.role === 'symetrique');
    const options = ['Figure ①', 'Figure ②', 'Figure ③', 'Figure ④'];
    const bonne = options[bonneIndex];
    const diag = axe.type === 'd1' || axe.type === 'd2';
    const texte = `<p>Sur chaque quadrillage, la figure bleue est la même et la droite (d) est le même axe ${NOM_AXE[axe.type]}. Une seule figure orange est le symétrique de la figure bleue par rapport à (d).${diag ? ' Rappel : quand l\'axe est en diagonale, on reporte les carreaux le long de l\'autre diagonale.' : ''}</p><p>Laquelle ?</p>`;
    const payload = makeQcm(options, bonne, texte, {
        ...opts(L_BON), elements_additionnels: imgElement(svgPlanche(axe, F, panneaux), 'large'),
    });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    payload._meta.params = { axe, F, bonneIndex, roles: panneaux.map((p) => p.role), signature: `${axe.type}${axe.c}-${F.map(cle).join('|')}-${bonneIndex}` };
    return payload;
};

// ─── Carte 2b : V/F sur la figure ──────────────────────────────────────────
// Figure : A et un point A′ (bien placé, translaté du même côté, ou à un carreau
// de trop), B sur l'axe ; lignes recalculées.

let dernieresLignesVfFigure1070 = [];

const genFigureVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const axe = tirerAxe();
        const A = tirerPoint(axe, 1, 3); const S = symetrique(A, axe); const n = normale(axe);
        const k = distanceAxe(A, axe);
        const variante = randomChoice(['bon', 'bon', 'translate', 'trop_loin']);
        let Ap;
        if (variante === 'bon') Ap = S;
        else if (variante === 'trop_loin') { const versLoin = (S[0] - A[0]) * n[0] + (S[1] - A[1]) * n[1] > 0 ? n : [-n[0], -n[1]]; Ap = [S[0] + versLoin[0], S[1] + versLoin[1]]; }
        else {
            // même distance, de l'autre côté, mais décalé le long de l'axe
            const u = axe.type === 'v' ? [0, 1] : axe.type === 'h' ? [1, 0] : axe.type === 'd1' ? [1, 1] : [1, -1];
            const dec = randomChoice([1, 2, -1, -2]);
            Ap = [S[0] + u[0] * dec, S[1] + u[1] * dec];
        }
        if (!dansGrille(Ap) || cle(Ap) === cle(A)) continue;
        // B sur l'axe, dans la grille
        let B = null;
        for (let t = 0; t < 50 && !B; t++) { const P = [randInt(0, N), randInt(0, N)]; if (surAxe(P, axe) && cle(P) !== cle(A) && cle(P) !== cle(Ap)) B = P; }
        if (!B) continue;
        const sg = (P) => { const [x, y] = P; const { type, c } = axe; return Math.sign(type === 'v' ? x - c : type === 'h' ? y - c : type === 'd1' ? y - x - c : x + y - c); };
        const estSym = cle(Ap) === cle(S);
        const kAp = distanceAxe(Ap, axe);
        const memeDistance = kAp === k;
        const autreCote = sg(A) !== sg(Ap);
        const nomA = `A${PRIME}`; const nomB = `B${PRIME}`;
        const pl = (n) => `${n} carreau${n > 1 ? 'x' : ''}`;
        // Une ligne numérique fausse porte une valeur voisine (jamais la vraie).
        const kFaux = k + randomChoice([1, 2]);
        const candidates = [
            { l: `${nomA} est le symétrique de A par rapport à (d).`, v: estSym },
            { l: `A et ${nomA} sont à la même distance de l'axe (d).`, v: memeDistance },
            { l: `A et ${nomA} sont de part et d'autre de l'axe (d).`, v: autreCote },
            { l: `Le symétrique de B par rapport à (d) est B lui-même.`, v: true },
            { l: `Le symétrique de B par rapport à (d) est un point ${nomB} différent de B.`, v: false },
            { l: `A est à ${pl(k)} de l'axe (d).`, v: true },
            { l: `A est à ${pl(kFaux)} de l'axe (d).`, v: false },
            { l: `${nomA} est à ${pl(Number.isInteger(kAp) ? kAp : k)} de l'axe (d).`, v: Number.isInteger(kAp) },
            { l: `${2 * k} carreaux séparent A de son symétrique par rapport à (d).`, v: true },
            { l: `${2 * k + randomChoice([-1, 1, 2])} carreaux séparent A de son symétrique par rapport à (d).`, v: false },
        ];
        // Nombre de faux VOULU tiré d'abord (1, 2 ou 3 à parts égales, revue 2026-09-04, A2),
        // puis autant de lignes fausses et le complément de lignes vraies.
        const fausses = shuffle(candidates.filter((it) => !it.v)); const vraies = shuffle(candidates.filter((it) => it.v));
        const nbFaux = Math.min(randInt(1, 3), fausses.length);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4) continue;
        // Jamais deux lignes de la même famille (B, distance de A, écart A-A′) dans un même tableau.
        const familles = [[3, 4], [5, 6], [8, 9]];
        if (familles.some(([i, j]) => lignes.includes(candidates[i]) && lignes.includes(candidates[j]))) continue;
        // Retour prof 2026-09-15 (A7) : quand A′ n'est PAS le symétrique de A, une ligne sur « son symétrique »
        // se lit comme si elle parlait de A′ ; on ne la sert alors qu'accompagnée de la ligne qui tranche
        // le statut de A′ (« A′ est le symétrique de A », fausse dans ce cas).
        if (!estSym && (lignes.includes(candidates[8]) || lignes.includes(candidates[9])) && !lignes.includes(candidates[0])) continue;
        // Fenêtre stricte : aucune ligne du tirage précédent (les lignes numériques changent avec k).
        // Si la fenêtre bloque tout (même k, même variante deux fois de suite), on la relâche après 120 essais.
        if (essai > 120) dernieresLignesVfFigure1070 = [];
        if (lignes.some((it) => dernieresLignesVfFigure1070.includes(it.l))) continue;
        dernieresLignesVfFigure1070 = lignes.map((it) => it.l);
        const diag = axe.type === 'd1' || axe.type === 'd2';
        const svg = svgUneGrille({ axe, points: [{ p: A, nom: 'A' }, { p: Ap, nom: nomA, couleur: '#B45309' }, { p: B, nom: 'B', couleur: '#15803d' }] });
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            `<p>Sur le quadrillage, la droite (d) est un axe ${NOM_AXE[axe.type]}.${diag ? ' En diagonale, on compte les carreaux le long de l\'autre diagonale.' : ''} Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            { ...opts(L_BON), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: variante };
        payload._meta.params = { axe, A, Ap, B, variante };
        return payload;
    }
    throw new Error('gen_1070 : V/F figure sans mixité après 160 essais');
};

// ─── Carte 3 : vrai ou faux ? ──────────────────────────────────────────────

const POOL_VF = [
    { l: 'La symétrie axiale conserve les longueurs.', v: true },
    { l: 'La symétrie axiale conserve les mesures des angles.', v: true },
    { l: 'Une figure et son symétrique ont la même aire.', v: true },
    { l: 'Un point situé sur l\'axe est son propre symétrique.', v: true },
    // « même distance de part et d'autre » et « distance = distance à l'axe » sont testées par
    // les lignes numériques : remplacées par le geste sur quadrillage (revue 2026-09-04, A8).
    { l: 'Sur quadrillage, on place le symétrique d\'un point en comptant les carreaux perpendiculairement à l\'axe.', v: true },
    { l: 'L\'axe de symétrie est la médiatrice du segment qui joint un point à son symétrique.', v: true },
    { l: 'Une figure et son symétrique sont du même côté de l\'axe.', v: false },
    { l: 'Le symétrique d\'un segment est un segment plus court.', v: false },
    { l: 'Quand l\'axe est en diagonale, on reporte les carreaux horizontalement.', v: false },
    { l: 'Le symétrique d\'un point situé sur l\'axe est de l\'autre côté de l\'axe.', v: false },
    { l: 'Sur quadrillage, on place le symétrique d\'un point en comptant les carreaux le long de l\'axe.', v: false },
    { l: 'Le symétrique d\'un triangle peut être un carré.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 6) throw new Error('gen_1070 : pool V/F déséquilibré');

let derniersServisVf1070 = [];

const genVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        const vraie1 = Math.random() < 0.5; const k1 = randInt(1, 5);
        const vraie2 = Math.random() < 0.5; const k2 = randInt(1, 5);
        const numeriques = [
            { l: `A est à ${k1} carreau${k1 > 1 ? 'x' : ''} de l'axe : son symétrique A${PRIME} est à ${vraie1 ? k1 : k1 + randomChoice([1, 2])} carreaux de l'axe, de l'autre côté.`, v: vraie1 },
            { l: `A est à ${k2} carreau${k2 > 1 ? 'x' : ''} de l'axe : ${vraie2 ? 2 * k2 : 2 * k2 + randomChoice([-1, 1, 2])} carreaux séparent A de son symétrique A${PRIME}.`, v: vraie2 },
        ];
        // A8 : lignes fixes déductibles des numériques exclues
        const dispo = POOL_VF.filter((it) => !derniersServisVf1070.includes(it.l));
        const premiere = randomChoice(dispo);
        // Les deux lignes « sur quadrillage » (perpendiculairement / le long de l'axe) ne sont jamais servies ensemble.
        const incompatible = (l) => /^Sur quadrillage, on place le symétrique/.test(l);
        const seconde = randomChoice(dispo.filter((it) => it !== premiere && !(incompatible(premiere.l) && incompatible(it.l))));
        const fixes = [premiere, seconde];
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        derniersServisVf1070 = [...derniersServisVf1070, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1070 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    carreaux_saisie: genCarreauxSaisie,
    bon_qcm_unique: genBonQcm,
    bon_tableau_vf: genFigureVf,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'carreaux', label: L_CARREAUX, formatsList: buildFormats('carreaux') },
    { id: 'bon', label: L_BON, formatsList: buildFormats('bon') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
